import { createServiceHTTPClientProvider, createBoundServiceQueryHooksCreator, createBoundServiceMutationHookCreator } from "@airhelp/service-utils";
import { a as fetchOffers, c as fetchFeatureFlags, g as fetchLounges, k as fetchAirPayoutJourneys, o as fetchAirLuggageJourneys, V as fetchJourney, Y as fetchJourneys, $ as fetchCheckDeletableJourney, a1 as deleteJourney, a5 as updateJourney, ac as addJourney, r as postPromoCode, af as cancelMembership, ai as fetchTrip, aj as updateAirPayoutJourneys, ak as updateAirLuggageJourneys } from "./update-air-luggage-journeys-DAg_1mM1.js";
import { useQueryClient } from "@tanstack/react-query";
const [AhPlusHTTPClientProvider, useAhPlusHTTPClient] = createServiceHTTPClientProvider({
  name: "AhPlus"
});
const AH_PLUS_REACT_QUERY_KEY_PREFIX = "ah-plus";
const createAhPlusQueryHooks = createBoundServiceQueryHooksCreator({
  queryKeyPrefix: AH_PLUS_REACT_QUERY_KEY_PREFIX,
  useClient: useAhPlusHTTPClient
});
const createAhPlusMutationHook = createBoundServiceMutationHookCreator({
  useClient: useAhPlusHTTPClient
});
const [useOffers, useOffersSuspense] = createAhPlusQueryHooks({
  queryFn: fetchOffers,
  queryKey: ["offers"]
});
const [useFeatureFlags, useFeatureFlagsSuspense] = createAhPlusQueryHooks({
  queryFn: fetchFeatureFlags,
  queryKey: ["feature-flags"]
});
const [useLounges, useLoungesSuspense] = createAhPlusQueryHooks({
  queryFn: fetchLounges,
  queryKey: ["lounges"]
});
const [useAirPayoutJourneys, useAirPayoutJourneysSuspense] = createAhPlusQueryHooks({
  queryFn: fetchAirPayoutJourneys,
  queryKey: ["journey", "air-payouts"]
});
const [useAirLuggageJourneys, useAirLuggageJourneysSuspense] = createAhPlusQueryHooks({
  queryFn: fetchAirLuggageJourneys,
  queryKey: ["journey", "air-luggages"]
});
const [useJourney, useJourneySuspense] = createAhPlusQueryHooks({
  queryFn: fetchJourney,
  queryKey: ["journey"]
});
const [useJourneys, useJourneysSuspense] = createAhPlusQueryHooks({
  queryFn: fetchJourneys,
  queryKey: ["journeys"]
});
const [useCheckDeletableJourney, useCheckDeletableJourneySuspense] = createAhPlusQueryHooks({
  queryFn: fetchCheckDeletableJourney,
  queryKey: ["journey", "check-deletable-journey"]
});
const useInternalDeleteJourney = createAhPlusMutationHook({
  mutationFn: deleteJourney
});
function useDeleteJourney(options) {
  const queryClient = useQueryClient();
  return useInternalDeleteJourney({
    async onSuccess(data, params, context) {
      var _a;
      await queryClient.invalidateQueries({
        queryKey: useJourneys.getQueryKey()
      });
      queryClient.removeQueries({
        queryKey: useJourney.getQueryKey({
          params
        })
      });
      await ((_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context));
    }
  });
}
const useInternalUpdateJourney = createAhPlusMutationHook({
  mutationFn: updateJourney
});
function useUpdateJourney(options) {
  const queryClient = useQueryClient();
  return useInternalUpdateJourney({
    async onSuccess(data, params, context) {
      var _a;
      await queryClient.setQueryData(
        useJourney.getQueryKey({ params: { id: data.id } }),
        { ...data, computed: params.journey.computed }
      );
      await queryClient.invalidateQueries({
        queryKey: useJourneys.getQueryKey()
      });
      await ((_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context));
    }
  });
}
const useInternalAddJourney = createAhPlusMutationHook({
  mutationFn: addJourney
});
function useAddJourney(options) {
  const queryClient = useQueryClient();
  return useInternalAddJourney({
    async onSuccess(data, params, context) {
      var _a;
      await queryClient.invalidateQueries({
        queryKey: useJourneys.getQueryKey()
      });
      await ((_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context));
    }
  });
}
const useApplyPromoCode = createAhPlusMutationHook({
  mutationFn: postPromoCode
});
const useCancelMembership = createAhPlusMutationHook({
  mutationFn: cancelMembership
});
const [useTrip, useTripSuspense] = createAhPlusQueryHooks({
  queryFn: fetchTrip,
  queryKey: ["trip"]
});
const useInternalUpdateAirPayoutJourneys = createAhPlusMutationHook({
  mutationFn: updateAirPayoutJourneys
});
function useUpdateAirPayoutJourneys(options) {
  const queryClient = useQueryClient();
  return useInternalUpdateAirPayoutJourneys({
    async onSuccess(data, params, context) {
      var _a;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: useJourney.getQueryKey({
            params: { id: params.journeyId }
          })
        }),
        queryClient.invalidateQueries({
          queryKey: useAirPayoutJourneys.getQueryKey({
            params: { journeyId: params.journeyId }
          })
        })
      ]);
      queryClient.setQueryData(
        useJourney.getQueryKey({ params: { id: params.journeyId } }),
        data
      );
      (_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context);
    }
  });
}
const useInternalUpdateAirLuggageJourneys = createAhPlusMutationHook({
  mutationFn: updateAirLuggageJourneys
});
function useUpdateAirLuggageJourneys(options) {
  const queryClient = useQueryClient();
  return useInternalUpdateAirLuggageJourneys({
    async onSuccess(data, params, context) {
      var _a;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: useJourney.getQueryKey({
            params: { id: params.journeyId }
          })
        }),
        queryClient.invalidateQueries({
          queryKey: useAirLuggageJourneys.getQueryKey({
            params: { journeyId: params.journeyId }
          })
        })
      ]);
      queryClient.setQueryData(
        useJourney.getQueryKey({ params: { id: params.journeyId } }),
        data
      );
      (_a = options == null ? void 0 : options.onSuccess) == null ? void 0 : _a.call(options, data, params, context);
    }
  });
}
export {
  AH_PLUS_REACT_QUERY_KEY_PREFIX,
  AhPlusHTTPClientProvider,
  useAddJourney,
  useAhPlusHTTPClient,
  useAirLuggageJourneys,
  useAirLuggageJourneysSuspense,
  useAirPayoutJourneys,
  useAirPayoutJourneysSuspense,
  useApplyPromoCode,
  useCancelMembership,
  useCheckDeletableJourney,
  useCheckDeletableJourneySuspense,
  useDeleteJourney,
  useFeatureFlags,
  useFeatureFlagsSuspense,
  useJourney,
  useJourneySuspense,
  useJourneys,
  useJourneysSuspense,
  useLounges,
  useLoungesSuspense,
  useOffers,
  useOffersSuspense,
  useTrip,
  useTripSuspense,
  useUpdateAirLuggageJourneys,
  useUpdateAirPayoutJourneys,
  useUpdateJourney
};
//# sourceMappingURL=react.js.map
